// API Service for TerraHydro Admin

import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;
const ADMIN_URL = `${BACKEND_URL}/api/admin`;

// Create axios instance with interceptor
const api = axios.create({
  baseURL: BACKEND_URL,
});

// Add token to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors (unauthorized)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      window.location.href = '/dashboard/login';
    }
    return Promise.reject(error);
  }
);

// ============= AUTH =============

export const authAPI = {
  login: async (email, password) => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);
    
    const response = await api.post(`${ADMIN_URL}/auth/login`, formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
    return response.data;
  },
  
  getCurrentUser: async () => {
    const response = await api.get(`${ADMIN_URL}/auth/me`);
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await api.put(`${ADMIN_URL}/auth/change-password`, {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  }
};

// ============= PRODUCTS =============

export const productsAPI = {
  list: async (params = {}) => {
    const response = await api.get(`${ADMIN_URL}/products`, { params });
    return response.data;
  },
  
  get: async (id) => {
    const response = await api.get(`${ADMIN_URL}/products/${id}`);
    return response.data;
  },
  
  create: async (data) => {
    const response = await api.post(`${ADMIN_URL}/products`, data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`${ADMIN_URL}/products/${id}`, data);
    return response.data;
  },
  
  delete: async (id) => {
    await api.delete(`${ADMIN_URL}/products/${id}`);
  },
  
  importExcel: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`${ADMIN_URL}/products/import`, formData);
    return response.data;
  },
  
  downloadTemplate: async () => {
    const response = await api.get(`${ADMIN_URL}/products/template`, {
      responseType: 'blob'
    });
    return response.data;
  },
  
  exportExcel: async (category = null) => {
    const params = category ? { category } : {};
    const response = await api.get(`${ADMIN_URL}/products/export`, {
      params,
      responseType: 'blob'
    });
    return response.data;
  }
};

// ============= CATEGORIES =============

export const categoriesAPI = {
  list: async () => {
    const response = await api.get(`${ADMIN_URL}/categories`);
    return response.data;
  },
  
  create: async (data) => {
    const response = await api.post(`${ADMIN_URL}/categories`, data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`${ADMIN_URL}/categories/${id}`, data);
    return response.data;
  },
  
  delete: async (id) => {
    await api.delete(`${ADMIN_URL}/categories/${id}`);
  }
};

// ============= BANNERS =============

export const bannersAPI = {
  list: async (params = {}) => {
    const response = await api.get(`${ADMIN_URL}/banners`, { params });
    return response.data;
  },
  
  create: async (data) => {
    const response = await api.post(`${ADMIN_URL}/banners`, data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`${ADMIN_URL}/banners/${id}`, data);
    return response.data;
  },
  
  delete: async (id) => {
    await api.delete(`${ADMIN_URL}/banners/${id}`);
  }
};

// ============= PAGE CONTENTS =============

export const pageContentsAPI = {
  list: async () => {
    const response = await api.get(`${ADMIN_URL}/page-contents`);
    return response.data;
  },
  
  get: async (pageKey) => {
    const response = await api.get(`${ADMIN_URL}/page-contents/${pageKey}`);
    return response.data;
  },
  
  update: async (pageKey, data) => {
    const response = await api.put(`${ADMIN_URL}/page-contents/${pageKey}`, data);
    return response.data;
  }
};

// ============= SETTINGS =============

export const settingsAPI = {
  get: async () => {
    const response = await api.get(`${ADMIN_URL}/settings`);
    return response.data;
  },
  
  update: async (data) => {
    const response = await api.put(`${ADMIN_URL}/settings`, data);
    return response.data;
  }
};

// ============= MEDIA =============

export const mediaAPI = {
  list: async (folder = null) => {
    const params = folder ? { folder } : {};
    const response = await api.get(`${ADMIN_URL}/media`, { params });
    return response.data;
  },
  
  upload: async (file, alt = '', caption = '', folder = '') => {
    const formData = new FormData();
    formData.append('file', file);
    if (alt) formData.append('alt', alt);
    if (caption) formData.append('caption', caption);
    if (folder) formData.append('folder', folder);
    
    const response = await api.post(`${ADMIN_URL}/upload`, formData);
    return response.data;
  },
  
  importUrl: async (url, alt = '', folder = '') => {
    const response = await api.post(`${ADMIN_URL}/media/import-url`, { url, alt, folder });
    return response.data;
  },
  
  importMultiple: async (urls, folder = '') => {
    const response = await api.post(`${ADMIN_URL}/media/import-multiple`, { urls, folder });
    return response.data;
  },
  
  scrapePage: async (url) => {
    const response = await api.post(`${ADMIN_URL}/media/scrape-page`, { url });
    return response.data;
  },
  
  getFolders: async () => {
    const response = await api.get(`${ADMIN_URL}/media/folders`);
    return response.data;
  },
  
  delete: async (id) => {
    await api.delete(`${ADMIN_URL}/media/${id}`);
  }
};

// ============= DASHBOARD STATS =============

export const dashboardAPI = {
  getStats: async () => {
    const response = await api.get(`${ADMIN_URL}/stats`);
    return response.data;
  }
};

// ============= PUBLIC API (for frontend) =============

export const publicAPI = {
  getSettings: async () => {
    const response = await api.get(`${API_URL}/settings`);
    return response.data;
  },
  
  getCategories: async () => {
    const response = await api.get(`${API_URL}/categories`);
    return response.data;
  },
  
  getProducts: async (params = {}) => {
    const response = await api.get(`${API_URL}/products`, { params });
    return response.data;
  },
  
  getProduct: async (id) => {
    const response = await api.get(`${API_URL}/products/${id}`);
    return response.data;
  },
  
  getBanners: async (position = null) => {
    const params = position ? { position } : {};
    const response = await api.get(`${API_URL}/banners`, { params });
    return response.data;
  },
  
  getPageContent: async (pageKey) => {
    const response = await api.get(`${API_URL}/page-contents/${pageKey}`);
    return response.data;
  }
};

export default api;
